// frontend/src/pages/OwnerSupplierQuotationsPage.js  (replacement)
//
// Supplier devis received by the owner. Same data layer and the same three
// actions (accept / reject / counter); only the presentation changed, onto the
// chrome/ink system so it matches the rest of the owner space.
//
// Two behavioural fixes came with it: alert() is replaced by inline state (an
// alert() blocks the tab and says nothing about which devis it refers to), and
// the counter input no longer relies on `dark:` variants that never apply —
// this app does not toggle a `dark` class, so those inputs were rendering
// black-on-white inside a dark panel.

import React, { useEffect, useState } from "react";
import { useAuth } from "../context/AuthContext";
import { useNavigate } from "react-router-dom";
import API_URL from "../api/baseURL";
import {
  Page, Panel, Button, Badge, Table, Tr, Td,
  EmptyState, Loading, ErrorNote, Note, inputClass,
} from "../components/ui/Iparc";
import { fmt } from "../theme/iparc";

// Mirrors QuotationResponse.status.
const STATUS = {
  accepted: { label: "Accepté", tone: "ok" },
  rejected: { label: "Rejeté", tone: "danger" },
  submitted: { label: "En attente", tone: "warn" },
  client_countered: { label: "Contre-offre envoyée", tone: "neutral" },
  supplier_countered: { label: "Contre-offre reçue", tone: "warn" },
};
const statusOf = (s) => STATUS[s] || { label: String(s || "—").replace(/_/g, " "), tone: "neutral" };

const fmtDate = (d) => {
  try { return d ? new Date(d).toLocaleDateString("fr-FR") : "—"; } catch { return "—"; }
};

const vehicleOf = (q) => {
  const car = q?.rfq?.car;
  const snap = q?.rfq?.vehicleSnapshot;
  return {
    label: [car?.brand || snap?.make, car?.model || snap?.model].filter(Boolean).join(" ") || "Véhicule",
    year: car?.year || snap?.year,
    plate: car?.plateNumber,
    vin: car?.vin,
  };
};

export default function OwnerSupplierQuotationsPage() {
  const { token, user } = useAuth();
  const navigate = useNavigate();

  // Shared between the owner and workshop spaces. The listing endpoint matches
  // RFQs on owner OR workshop, so both see exactly the ones they raised; only
  // the routes to navigate to differ.
  const isWorkshop = /workshop/i.test(user?.role || '');
  const newRequestPath = isWorkshop ? '/workshop/parts-request' : '/quotation/request';
  const invoicePath = (id) => (isWorkshop ? `/workshop/invoices` : `/owner/invoices/${id}`);

  const [supplierQuotes, setSupplierQuotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [counterPrice, setCounterPrice] = useState({});
  const [actionLoading, setActionLoading] = useState({});
  const [actionError, setActionError] = useState({});
  const [actionOk, setActionOk] = useState({});

  useEffect(() => {
    if (!token) return;
    const fetchQuotes = async () => {
      setLoading(true);
      try {
        const res = await fetch(`${API_URL}/quotations/supplier-quotations/owner`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) throw new Error("Échec du chargement");
        setSupplierQuotes(await res.json());
        setError("");
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    fetchQuotes();
  }, [token]);

  const handleAction = async (action, id, price) => {
    setActionError((prev) => ({ ...prev, [id]: "" }));
    setActionOk((prev) => ({ ...prev, [id]: "" }));

    if (action === "counter") {
      const numPrice = Number(price);
      if (!price || Number.isNaN(numPrice) || numPrice <= 0) {
        setActionError((prev) => ({ ...prev, [id]: "Entrez un prix valide supérieur à 0." }));
        return;
      }
    }

    setActionLoading((prev) => ({ ...prev, [id]: action }));
    try {
      let url = "";
      let method = "POST";
      let body = {};

      if (action === "accept") {
        url = `${API_URL}/quotations/accept/${id}`;
        method = "PUT";
      } else if (action === "reject") {
        url = `${API_URL}/quotations/responses/${id}/reject`;
      } else if (action === "counter") {
        url = `${API_URL}/quotations/responses/${id}/counter`;
        body = { price: Number(price) };
      }

      const res = await fetch(url, {
        method,
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: Object.keys(body).length ? JSON.stringify(body) : undefined,
      });

      const data = await res.json();
      if (!res.ok) throw new Error(data.error || `Action ${action} échouée`);

      if (action === "accept" && data.supplierInvoice?.id) {
        navigate(invoicePath(data.supplierInvoice.id));
        return;
      }

      setSupplierQuotes((prev) =>
        prev.map((q) => (q._id === id
          ? { ...q, status: data.status || action, counters: data.counters }
          : q))
      );

      if (action === "counter") setCounterPrice((prev) => ({ ...prev, [id]: "" }));

      setActionOk((prev) => ({
        ...prev,
        [id]: { accept: "Devis accepté.", reject: "Devis rejeté.", counter: "Contre-offre envoyée." }[action],
      }));
    } catch (err) {
      console.error(`[${action}] error:`, err);
      setActionError((prev) => ({ ...prev, [id]: err.message }));
    } finally {
      setActionLoading((prev) => ({ ...prev, [id]: null }));
    }
  };

  const pending = supplierQuotes.filter(
    (q) => q.status === "submitted" || q.status === "supplier_countered"
  ).length;

  return (
    <Page
      subtitle="Pièces détachées"
      title="Devis fournisseurs"
      actions={
        <Button onClick={() => navigate(newRequestPath)}>Nouvelle demande</Button>
      }
    >
      {error && <ErrorNote>{error}</ErrorNote>}

      {loading ? (
        <Panel><Loading label="Chargement des devis…" /></Panel>
      ) : supplierQuotes.length === 0 ? (
        <Panel>
          <EmptyState
            title="Aucun devis reçu"
            hint="Les devis envoyés par vos fournisseurs apparaîtront ici."
            action={<Button onClick={() => navigate(newRequestPath)}>Demander un devis</Button>}
          />
        </Panel>
      ) : (
        <>
          {pending > 0 && (
            <Note tone="warn">
              {pending} devis {pending > 1 ? "attendent" : "attend"} votre réponse.
            </Note>
          )}

          {supplierQuotes.map((quote) => {
            const st = statusOf(quote.status);
            const veh = vehicleOf(quote);
            const busy = !!actionLoading[quote._id];
            const canAct = quote.status === "submitted" || quote.status === "supplier_countered";

            return (
              <Panel
                key={quote._id}
                title={quote.supplier?.businessName || quote.supplier?.name || "Fournisseur"}
                action={<Badge tone={st.tone}>{st.label}</Badge>}
              >
                {/* Identity strip: who, which car, when. */}
                <div className="grid gap-3.5 [grid-template-columns:repeat(auto-fit,minmax(200px,1fr))] pb-3.5 border-b border-[#241F1B]">
                  <div>
                    <p className="text-[10px] uppercase tracking-[.16em] text-[#6E665C] mb-1">Contact</p>
                    <p className="text-[12.5px] text-[#C9C3B6] truncate">{quote.supplier?.email || "—"}</p>
                    {quote.supplier?.phone && (
                      <p className="text-[12.5px] text-[#8F877B]">{quote.supplier.phone}</p>
                    )}
                  </div>
                  <div>
                    <p className="text-[10px] uppercase tracking-[.16em] text-[#6E665C] mb-1">Véhicule</p>
                    <p className="text-[12.5px] text-[#F4F1EA]">
                      {veh.label} {veh.year && <span className="text-[#8F877B]">({veh.year})</span>}
                    </p>
                    {veh.plate && (
                      <p className="font-['Space_Grotesk',sans-serif] text-[12px] text-[#C9C3B6]">{veh.plate}</p>
                    )}
                  </div>
                  <div>
                    <p className="text-[10px] uppercase tracking-[.16em] text-[#6E665C] mb-1">Reçu le</p>
                    <p className="text-[12.5px] text-[#C9C3B6] tabular-nums">{fmtDate(quote.createdAt)}</p>
                  </div>
                </div>

                {/* Lines */}
                <div className="pt-3.5">
                  <Table head={["Désignation", "Référence", "Marque", "Qté", "P.U.", "Total"]} minWidth={680}>
                    {(Array.isArray(quote.items) ? quote.items : []).map((item, idx) => {
                      // What the owner pays is the public price; supplierPrice
                      // is the supplier's own figure and must not be shown as
                      // the price on a client-facing page.
                      const unit = Number(item.publicUnitPrice ?? item.supplierPrice ?? 0);
                      const qty = Number(item.quantity) || 0;
                      return (
                        <Tr key={idx}>
                          <Td className="text-[#F4F1EA]">{item.description || "—"}</Td>
                          <Td mono>{item.reference || "—"}</Td>
                          <Td>{item.brand || "—"}</Td>
                          <Td mono className="text-center">{qty}</Td>
                          <Td mono className="text-right">{fmt(unit)}</Td>
                          <Td mono className="text-right text-[#F4F1EA]">{fmt(unit * qty)}</Td>
                        </Tr>
                      );
                    })}
                  </Table>

                  <div className="flex items-baseline justify-end gap-4 pt-3.5 mt-3.5 border-t border-[#241F1B]">
                    <span className="text-[10.5px] uppercase tracking-[.16em] text-[#6E665C]">Total</span>
                    <span className="font-['Space_Grotesk',sans-serif] tabular-nums text-[19px] font-semibold text-[#F4F1EA]">
                      {fmt(quote.totalPublic || quote.totalSupplier || 0)}{" "}
                      <span className="text-[12px] text-[#6E665C]">F</span>
                    </span>
                  </div>
                </div>

                {/* Negotiation history */}
                {Array.isArray(quote.counters) && quote.counters.length > 0 && (
                  <div className="mt-3.5 pt-3.5 border-t border-[#241F1B]">
                    <p className="text-[10px] uppercase tracking-[.16em] text-[#6E665C] mb-2">Négociation</p>
                    <div className="flex flex-col gap-1">
                      {quote.counters.map((c, idx) => (
                        <div key={idx} className="flex items-baseline justify-between gap-4 text-[12px]">
                          <span className="text-[#C9C3B6]">
                            {c.by === "client" ? "Vous" : "Le fournisseur"}
                          </span>
                          <span className="font-['Space_Grotesk',sans-serif] tabular-nums text-[#F4F1EA]">
                            {fmt(c.price)} F
                          </span>
                          <span className="text-[#5C554C] tabular-nums">{fmtDate(c.at)}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {actionError[quote._id] && (
                  <div className="mt-3.5"><ErrorNote>{actionError[quote._id]}</ErrorNote></div>
                )}
                {actionOk[quote._id] && (
                  <div className="mt-3.5"><Note tone="ok">{actionOk[quote._id]}</Note></div>
                )}

                {/* Actions */}
                <div className="flex flex-wrap gap-2.5 items-center mt-3.5 pt-3.5 border-t border-[#241F1B]">
                  {canAct ? (
                    <>
                      <Button
                        as="button" type="button" variant="primary"
                        onClick={() => handleAction("accept", quote._id)}
                        disabled={busy}
                      >
                        {actionLoading[quote._id] === "accept" ? "…" : "Accepter"}
                      </Button>
                      <Button
                        as="button" type="button" variant="danger"
                        onClick={() => handleAction("reject", quote._id)}
                        disabled={busy}
                      >
                        {actionLoading[quote._id] === "reject" ? "…" : "Rejeter"}
                      </Button>

                      <div className="flex items-center gap-2 ml-auto">
                        <input
                          type="number"
                          min="1"
                          placeholder="Votre prix (F)"
                          className={`${inputClass} w-[150px] h-[34px] text-right`}
                          value={counterPrice[quote._id] || ""}
                          onChange={(e) =>
                            setCounterPrice({ ...counterPrice, [quote._id]: e.target.value })
                          }
                        />
                        <Button
                          as="button" type="button"
                          onClick={() => handleAction("counter", quote._id, counterPrice[quote._id])}
                          disabled={busy}
                        >
                          {actionLoading[quote._id] === "counter" ? "…" : "Contre-offre"}
                        </Button>
                      </div>
                    </>
                  ) : (
                    <p className="text-[12.5px] text-[#8F877B]">
                      {quote.status === "client_countered"
                        ? "En attente de la réponse du fournisseur à votre contre-offre."
                        : quote.status === "accepted"
                          ? "Ce devis a été accepté."
                          : "Ce devis a été rejeté."}
                    </p>
                  )}
                </div>
              </Panel>
            );
          })}
        </>
      )}
    </Page>
  );
}
